/**
 * @file        src/components/AirlineLogo.jsx
 * @description Renders an airline's real logo from /public/airlines/<IATA>.<ext>
 *              (served at /airlines/<code>.<ext>; PNG unless listed in LOGO_EXT).
 *              Falls back to a colour-coded 2-letter badge when a code has no
 *              logo file or the image fails to load, so a printed document never
 *              shows a broken image.
 *
 *              This is a PORT of the customer site's components/AirlineLogo.jsx,
 *              kept field-for-field identical so the admin e-ticket and the
 *              customer's own voucher print the same mark at the same size. The
 *              admin console previously drew airline logos from a remote CDN
 *              (pics.avs.io) inside a white bordered box — a different image set,
 *              a white frame the customer copy does not have, and a network
 *              dependency that left the logo blank on an offline print.
 * @author      Kinetika Labs
 * @created     2026-09-09
 * @changelog
 *   - 2026-09-10 Claude — FIX: no logo ever rendered in the admin. The path was
 *                          root-absolute ("/airlines/<IATA>.png") as ported from
 *                          the customer site, but this app is built with
 *                          base:'/admin/' and serves its public assets under it —
 *                          so every request landed outside the base, errored, and
 *                          fell back to the colour-coded badge. Now built from
 *                          import.meta.env.BASE_URL. The asset set itself was
 *                          present and correct all along (474 files, identical to
 *                          the customer site's).
 *   - 2026-09-09 Claude — Initial. Ported from booking_frontends/neon so the two
 *                          documents match; /public/airlines copied across with it.
 */
import React, { useState, useEffect } from 'react';

// Codes whose logo file is not a .png. The bundled set is PNG, so this only
// lists the exceptions. Keep in sync with the customer site's copy.
const LOGO_EXT = { UK: 'svg' };

export default function AirlineLogo({ code, color, label, size = 40, className = '' }) {
  const iata = String(code || '').toUpperCase();
  // BASE_URL, not a root-absolute "/airlines/…". The admin console is built with
  // base:'/admin/' (vite.config.js), so it is served from /admin/ and its public
  // assets live at /admin/airlines/<IATA>.png. This component was ported verbatim
  // from the customer site, which IS served at the domain root — there "/airlines"
  // is correct, here it resolved outside the base, 404'd, and every logo silently
  // fell through to the two-letter badge (AI Express printed a slate "AI" circle
  // built from its NAME, which is also why it did not even read "IX").
  // import.meta.env.BASE_URL is Vite's own base and always ends in a slash, so
  // this stays correct if the console is ever moved or served from the root.
  const src = iata ? `${import.meta.env.BASE_URL}airlines/${iata}.${LOGO_EXT[iata] || 'png'}` : null;
  const [failed, setFailed] = useState(false);

  // Reset the error state when the airline changes, otherwise a previous miss
  // would hide a valid logo on a re-render with different props.
  useEffect(() => { setFailed(false); }, [iata]);

  const box = { width: size, height: size };

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={label || iata || 'Airline'}
        title={label || iata}
        onError={() => setFailed(true)}
        draggable="false"
        // objectFit/borderRadius inline (not a utility class) because the admin
        // console does not ship Tailwind — same rendering, different plumbing.
        style={{ ...box, objectFit: 'contain', borderRadius: 8, flexShrink: 0 }}
        className={className}
      />
    );
  }

  return (
    <div
      style={{
        ...box, background: color || '#475569', borderRadius: 8, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 700, color: '#fff', fontSize: size * 0.4,
      }}
      className={className}
    >
      {(label || iata || '··').slice(0, 2)}
    </div>
  );
}
